import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardSubtitle,
    IonCardTitle,
    IonChip,
    IonCol,
    IonIcon,
    IonRow
} from "@ionic/react";
import React, { useContext, useEffect, useState } from "react";
import { Barn, BarnMember, Person } from "../../models";
import ErrorAlert from "../Errors/ErrorAlert";
import { BarnPerson } from "../../interfaces/Person";
import { getStrictBarnPeople } from "../../utilities/barn/BarnPeople";
import AddBarnPersonCard from "../Barn/AddBarnPersonCard";
import { useSubscriptionByItself } from "../../utilities/subscription/Subscription";
import { onCreatePerson, onDeletePerson, onUpdatePerson } from "../../graphql/subscriptions";
import Spinner from "../Spinners/Spinner";
import BasicModal from "../Modals/BasicModal";
import { personSharp } from "ionicons/icons";
import BasicSelect, { formattedOption } from "../Select/BasicSelect";
import { UpdateBarnMemberInput } from "../../API";
import { getBarnMemberByBarnIdPersonId, updateBarnMember } from "../../utilities/barnMember/BarnMember";
import { PersonContext } from "../../context/PersonContext";

interface _Props {
    barn?: Barn
    onSelect: (barnPerson: BarnPerson) => void
}

const formattedOptions: formattedOption[] = [
    {
        value: "admin",
        label: "Admin Barn Member",
        object: "admin"
    },
    {
        value: "member",
        label: "Member Barn Member",
        object: "member"
    }
];

const EditPeopleTable: React.FC<_Props> = ({barn, onSelect}) => {
    const user = useContext(PersonContext);

    const createSubscription = useSubscriptionByItself({
        config: {
            query: onCreatePerson,
            key: "onCreatePerson"
        }
    });

    const updateSubscription = useSubscriptionByItself({
        config: {
            query: onUpdatePerson,
            key: "onUpdatePerson"
        }
    });

    const deleteSubscription = useSubscriptionByItself({
        config: {
            query: onDeletePerson,
            key: "onDeletePerson"
        }
    });

    const [isCurrentUserABarnAdmin, setIsCurrentUserABarnAdmin] = useState(false);
    const [previousBarn, setPreviousBarn] = useState<Barn | null>(null);
    const [people, setPeople] = useState<BarnPerson[] | null | undefined>();
    const [currentCreateSubscriptionItem, setCreateCurrentSubscriptionItem] = useState<any>();
    const [currentUpdateSubscriptionItem, setUpdateCurrentSubscriptionItem] = useState<any>();
    const [currentDeleteSubscriptionItem, setDeleteCurrentSubscriptionItem] = useState<any>();
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState<BarnPerson | null>(null);
    const [selectedPermission, setSelectedPermission] = useState<string | null>(null);
    const [error, setError] = useState("");

    const checkIfCurrentUserIsABarnAdmin = async (barn: Barn) => {
        if (user) {
            const query = await getBarnMemberByBarnIdPersonId(barn.id, user.id);
            if (query.isSuccess) {
                const barnMember: BarnMember = query.result;
                if (barnMember && barnMember.permissionLevel) {
                    setIsCurrentUserABarnAdmin(barnMember.permissionLevel === "admin");
                } else {
                    setIsCurrentUserABarnAdmin(false);
                }
            }
        }
    }

    const getPeople = async (barn: Barn) => {
        setIsLoading(true);
        const people = await getStrictBarnPeople(barn.id, barn.ownerPersonId || undefined);
        setPeople(people);
        setIsLoading(false);
    }

    const getData = async (barn: Barn) => {
        await getPeople(barn);
        setPreviousBarn(barn);
        await checkIfCurrentUserIsABarnAdmin(barn);
    }

    useEffect(() => {
        if ((barn && !people) || (barn && previousBarn && previousBarn.id !== barn?.id)) {
            getData(barn);
        }
    }, [barn]);

    useEffect(() => {
        if (createSubscription && createSubscription[0] !== undefined && createSubscription[0] !== currentCreateSubscriptionItem) {
            setCreateCurrentSubscriptionItem(createSubscription[0]);
            if (barn) getPeople(barn);
        }
    }, [createSubscription]);

    useEffect(() => {
        if (updateSubscription && updateSubscription[0] !== undefined && updateSubscription[0] !== currentUpdateSubscriptionItem) {
            setUpdateCurrentSubscriptionItem(updateSubscription[0]);
            if (barn) getPeople(barn);
        }
    }, [updateSubscription]);

    useEffect(() => {
        if (deleteSubscription && deleteSubscription[0] !== undefined && deleteSubscription[0] !== currentDeleteSubscriptionItem) {
            setDeleteCurrentSubscriptionItem(deleteSubscription[0]);
            if (barn) getPeople(barn);
        }
    }, [deleteSubscription]);

    const onClickMemberChip = (person: BarnPerson) => {
        if (isCurrentUserABarnAdmin) {
            setIsModalOpen(true);
            setSelectedPerson(person);
            setSelectedPermission(person.isBarnAdmin ? "admin" : "member");
        }
    }

    const onCloseModal = () => {
        setIsModalOpen(false);
        setSelectedPerson(null);
        setSelectedPermission(null);
    }

    const handleOnChange = (formattedOptionObject: string) => {
        setSelectedPermission(formattedOptionObject);
    }
    
    const handleSave = async () => {
        setIsLoading(true);
        setError("");
        if (people && selectedPerson && selectedPerson.barnMember) {
            // Update in DB
            const updateInput: UpdateBarnMemberInput = {
                id: selectedPerson.barnMember.id,
                permissionLevel: selectedPermission
            }
            const updateResult = await updateBarnMember(updateInput);
            if (updateResult.isSuccess) {
                // Update in local state
                const updatedBarnMember: BarnMember = updateResult.result;
                const index = people.indexOf(selectedPerson);
                const updatedBarnPerson: BarnPerson = {
                    ...selectedPerson,
                    isBarnAdmin: selectedPermission === "admin" ? true : false,
                    barnMember: updatedBarnMember
                }
                const updatedPeople: BarnPerson[] = [
                    ...people.slice(0, index),
                    updatedBarnPerson,
                    ...people.slice(index + 1)
                ];
                setPeople(updatedPeople);
            } else {
                setError(updateResult.message || "An error occurred while updating the barn member.");
            }
        }
        setIsModalOpen(false);
        setSelectedPerson(null);
        setIsLoading(false);
    }

    const onSelectPerson = (person: BarnPerson) => {
        onSelect(person);
    }

    return (
        <>
        <IonCard mode="md" className="ion-padding bg-white">
            <IonCardTitle>
                <IonRow>
                    <IonCol sizeXs="12" sizeMd="4">
                        Barn Members
                    </IonCol>
                    <IonCol offsetMd="4" sizeXs="12" sizeMd="4" className="ion-text-right">
                        <AddBarnPersonCard barn={barn} />
                    </IonCol>
                </IonRow>
            </IonCardTitle>
            <IonCardSubtitle>Click on a Person record to edit it.</IonCardSubtitle>
            {isLoading ?
                <Spinner />
                :
                <>
                    {error && <ErrorAlert width="12" error={error}/>}
                    {(people && people.length > 0) ?
                        <>
                            {people.map((person, i) => (
                                <IonRow key={i}>
                                    <IonCol>
                                        <IonCard>
                                            <IonCardTitle className="m-3">
                                                <IonRow>
                                                    <IonCol sizeXs="12" sizeMd="8">{person.name}</IonCol>
                                                    <IonCol sizeXs="12" sizeMd="4">
                                                        <IonButton color="tertiary" onClick={() => onSelectPerson(person)}>
                                                            <IonIcon icon={personSharp} /> View Person
                                                        </IonButton>
                                                    </IonCol>
                                                </IonRow>
                                            </IonCardTitle>
                                            <IonCardContent>
                                                <IonRow>
                                                    <IonCol sizeXs="12" sizeMd="4">
                                                        <h3>User Account</h3>
                                                        <p>{person.personId ? ("Has a user account. " + person.person?.email || "") : "No user account found. "}</p>
                                                        <p>
                                                            {person.isBarnOwner ? <IonChip className="rounded-2 font-weight-bold bg-dark text-white mx-0">Owner</IonChip> : ""} { } 
                                                            {person.isBarnAdmin === true ? 
                                                                <IonChip onClick={() => onClickMemberChip(person)} className="rounded-2 font-weight-bold bg-info text-white mx-0">Admin</IonChip> 
                                                                : 
                                                                person.isBarnAdmin === false ? 
                                                                    <IonChip onClick={() => onClickMemberChip(person)} className="rounded-2 font-weight-bold bg-info text-white mx-0">Member</IonChip> 
                                                                    : 
                                                                    ""
                                                            }
                                                        </p>
                                                    </IonCol>
                                                    <IonCol sizeXs="12" sizeMd="4">
                                                        <h3>Roles</h3>
                                                        {(person.fullRoles && person.fullRoles.length > 0) ?
                                                            <>
                                                                {person.fullRoles.map((role, i) => (
                                                                    <p key={i}>{role.role}</p>
                                                                ))}
                                                            </>
                                                            :
                                                            <p>No roles found.</p>
                                                        }
                                                    </IonCol>
                                                    <IonCol sizeXs="12" sizeMd="4">
                                                        <h3>Created By</h3>
                                                        <p>{person.creatorName || "No creator found."}</p>
                                                    </IonCol>
                                                </IonRow>                                                
                                            </IonCardContent>
                                        </IonCard>
                                    </IonCol>
                                </IonRow>
                            ))}
                        </>
                        :
                        <p>No people found.</p>
                    }
                </>
            }
        </IonCard>
        <BasicModal 
            isOpen={isModalOpen} 
            onClose={() => onCloseModal()} 
            title="Update Permission"
            isLoading={isLoading}
            errorMessage={error}
            footerButtons={[
                {
                    text: "Cancel",
                    color: "light",
                    disabled: isLoading,
                    onClick: onCloseModal,
                },
                {
                    text: "SAVE",
                    loadingText: "Saving...",
                    color: "success",
                    disabled: isLoading || !selectedPerson,
                    onClick: handleSave,
                },
        ]}>
            <IonRow className="ion-align-items-center">
                <IonCol className="ion-text-center" size="12">
                    <p>Update the permissions for the person: {selectedPerson?.name}</p>
                </IonCol>
                <IonCol className="ion-text-center" size="12">
                    <BasicSelect
                        formattedValues={formattedOptions}
                        selectedValue={selectedPermission ? selectedPermission : null}
                        isClearable={false}
                        isDisabled={false}
                        onSelect={handleOnChange}
                    />
                </IonCol>
            </IonRow>
        </BasicModal>
        </>
    );
};

export default EditPeopleTable;